/**
 * Compact count-only summary of SSI symptom / domain decisions.
 *
 * Used in the SSI Review completion dialog (SLaM / 3 Borough) so clinicians
 * see totals at a glance (e.g. "5 / 9") rather than a line-by-line breakdown.
 * Full per-symptom tables remain available via the in-page summary dialogs.
 */

import React from 'react';

/**
 * @param {Object} props
 * @param {{ adulthoodTotal: number, childhoodTotal: number }} props.attentionTotals
 * @param {{ adulthoodTotal: number, childhoodTotal: number }} props.hyperactivityTotals
 * @param {Array<{ adulthood?: boolean, childhood?: boolean }>} props.domainImpairmentData
 */
const SSIDecisionCountSummary = ({
  attentionTotals = { adulthoodTotal: 0, childhoodTotal: 0 },
  hyperactivityTotals = { adulthoodTotal: 0, childhoodTotal: 0 },
  domainImpairmentData = [],
}) => {
  const domainTotal = domainImpairmentData.length;
  const domainsAdulthood = domainImpairmentData.filter((d) => d.adulthood).length;
  const domainsChildhood = domainImpairmentData.filter((d) => d.childhood).length;

  const rows = [
    {
      label: 'Inattention',
      adulthood: `${attentionTotals.adulthoodTotal} / 9`,
      childhood: `${attentionTotals.childhoodTotal} / 9`,
    },
    {
      label: 'Hyperactivity / Impulsivity',
      adulthood: `${hyperactivityTotals.adulthoodTotal} / 9`,
      childhood: `${hyperactivityTotals.childhoodTotal} / 9`,
    },
    {
      label: 'Domains of Impairment',
      adulthood: domainTotal > 0 ? `${domainsAdulthood} / ${domainTotal}` : '—',
      childhood: domainTotal > 0 ? `${domainsChildhood} / ${domainTotal}` : '—',
    },
  ];

  return (
    <table className="w-full text-left shadow-md rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: '50%' }} />
        <col style={{ width: '25%' }} />
        <col style={{ width: '25%' }} />
      </colgroup>
      <thead>
        <tr className="bg-primary text-white">
          <th className="p-3 text-sm">Area</th>
          <th className="p-3 text-sm text-center">Adulthood</th>
          <th className="p-3 text-sm text-center">Childhood</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="bg-primary/5 border-b border-primary/10">
            <td className="p-3 text-sm font-semibold text-primary">{row.label}</td>
            <td className="p-3 text-sm text-center bg-white font-medium text-gray-800">
              {row.adulthood}
            </td>
            <td className="p-3 text-sm text-center bg-white font-medium text-gray-800">
              {row.childhood}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default SSIDecisionCountSummary;
